<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton 
                :showBtn="false"
                :showAddNew="false"
                title="Create Bill"
            />
        </div>

        <div class="my-2 px-2">
            <TableHeaderIPDBillAdd
                :formData="formData"
                :patient="patient"
                :tableItems="tableItems"
                from="add"
            />
        </div>

        <div class="col-12 px-2">
            <ListTable 
                :tableItems="tableItems"
                @addNewItem="addNewItem"
                :vatRate="vatRate"
                :salesAccountHeads="salesAccountHeads"
                :discountAccountHeads="discountAccountHeads"
            />
        </div>
        <div class="row px-2">
            <div class="col-md-6">
                <div class="mb-1">
                    <label class="form-label" for="description">Ledger Memo</label>
                    <vField
                        as="textarea"
                        name="description"
                        type="number"
                        class="form-control"
                        v-model="formData.description"
                    />
                </div>
            </div>
            <div class="col-md-5 ml-auto mt-auto mb-1">
                <div class="d-flex flex-wrap gap-1 gy-2">
                    <button :disabled="loading" @click="handleSubmit(true)" class="btn btn-primary">Save</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {inject, onMounted, provide, reactive, ref, computed} from 'vue';
import TitleButton from '@/components/atom/TitleButton';
import ListTable from '@/components/molecule/company/dental-os/service/billing/DentalHospitalTable.vue';
import {useRoute, useRouter} from 'vue-router';
import {generateUUID} from '@/services/utils/global';
import handlePurchase from '@/services/modules/purchase';
import {vatRate} from '@/data/inventory.js';
import handleCompany from "@/services/modules/company";
import dentalPdfPrinterHelper from '@/services/utils/pdf/dental/dentalPdfPrinterHelper';
import TableHeaderIPDBillAdd from "@/components/molecule/company/dental-os/service/billing/TableHeaderHospitalBillAdd.vue";
import handleHospital from "@/services/modules/hospital";
import useDate from "@/services/utils/day";
import { cloneDeep } from 'lodash';

const { generatePdf } = dentalPdfPrinterHelper();
const { fetchProductList, fetchSalesBill, ...rest } = handlePurchase();
const { fetchCompanyInfo } = handleCompany();
const {fetchSingleNote} = handleHospital();
const $route = useRoute();
const $router = useRouter();
const companyId = $route.params.companyId;
const loader = ref(true);
const loading = ref(false);
const salesAccountHeads = ref([]);
const discountAccountHeads = ref([]);
const totals = ref({});
const invoiceRes = ref({});
const company = ref({});
const patient = ref({});
const dateTime = useDate();
const isCard = computed(() => {
    return $route.query.isCard
})

const emit = defineEmits(['resetLatestSale']);

const showSuccess =  inject('showSuccess');
const showError =  inject('showError');

let tableItems = reactive([]);
let formData = ref({
    company_id: companyId,
    contact_profile_id: null,
    account_head_id: null,
    ipd_register_id: null,
    referrer_id: null,
    vat_payable_account_head_id: null,
    mop_account_head_id: null,
    receipt_reference: null,
    receipt_description: "IPD bill payment",
    payment: null,
    project_id: null,
    cost_centre_id: null,
    location_id: null,
    discount_head_id: null,
    adjustment_amount: 0,
    service_charge_head_id: null,
    service_charge_amount: 0,
    status: 'active',
    bill_number: '',
    date: '',
    has_item_detail: true,
    description: '',
    order_number: null,
    sales_person_id: null,
    commission_percent: null,
    lc_number: null,
    item_details: []
});

provide('formData', formData);
provide('totals', totals);

const addNewItem = () => {
    tableItems.push({
        id: generateUUID(),
        product_id: null,
        name: '',
        description: '',
        quantity : 1,
        rate: 0,
        discount_percent: 0,
        vat: 15,
        sub_total: 0,
        discount_amount: 0,
        vat_amount: 0,
        total: 0,
        is_edit: false,
        dentition_pertains: [],
    });
}

const handleSubmit = async (print=false) => {

    const copyFormData = getFormattedFormData();

    if (
        copyFormData.payment < 1 
        && !confirm("Paid amount is 0. Are you sure you want to save this invoice as Due?")
    ) {
        return;
    }

    loading.value = true;

    await rest.storeBillPayment(copyFormData).then(res => {  
            if(res.status) {
                showSuccess(res.message)
                if(print) goToPrint(res.data.id)
                emit('resetLatestSale');
                return;
            }
            showError(res.message)
        })
        .catch(err=> {
            console.error(err);
        })
        .finally(res => {
            loading.value = false;
        })
}

const getFormattedFormData = () => {
    const copyFormData = cloneDeep(formData.value);

    copyFormData.item_details = tableItems.map((item) => {
        item.contact_profile_id = item.service_resource ? item.service_resource.id : null;
        return item;
    });

    copyFormData.sale_type = "ipd_invoice";
    copyFormData.receipt_type = "ipd_invoice";
    if(!copyFormData.date) copyFormData.date = dateTime.currentDate();
    copyFormData.sales_order_master_id = $route.query.order_master_id || null;
    return copyFormData;
}

const goToPrint = (id) => {
    const companyQuery = `?company_id=${companyId}`;
    fetchSalesBill(id, companyQuery).then( (res) =>{
        if( res ){
            invoiceRes.value = res.data;
        }
    }).then( () => {
        generatePdf(company.value, invoiceRes.value, 'BILL', true, true)
    }).catch( (err) => {
        console.log(err);
    })
}

onMounted(() => {

    const companyQuery = `?company_id=${companyId}`;
    
    Promise.all([
        rest.fetchAccountPayable(companyQuery).then(res=> {
            if(res.data) formData.value.account_payable_head_id = res.data[0].id;
        }),
        rest.fetchAccountReceivable(companyQuery).then(res=> {
            if(res.data) formData.value.account_head_id = res.data[0].id;
        }),
        rest.fetchVatPayable(companyQuery).then(res=> {
            if(res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
        }),
        fetchCompanyInfo(companyId).then(res=> {
            if(res.data) company.value = res.data;
        }),
        rest.getAccountHeadBySlag("discount_account", companyQuery).then(res => {
            if(res.data) discountAccountHeads.value = res.data
        }),
        rest.getAccountHeadBySlag("sales_accounts", companyQuery).then(res => {
          if(res.data) salesAccountHeads.value = res.data
        }),
        fetchSingleNote(companyQuery, $route.query.ipd_register_id).then(res => {
            if (res.data) {
                patient.value = res.data;
                formData.value.contact_profile_id = res.data.patient.id
                formData.value.ipd_register_id = res.data.id
                formData.value.service_resource_id = res.data.human_resource_id
            }
        })
    ])
    .then(() => {loader.value=false})
    .catch(()=>{loader.value=false})
})

</script>
<style scoped>
address{
    margin-bottom: 0 !important;
}
</style>